// src/pages/accounting/ProjectPlanningPage.jsx
// Extracted from App.js - Project Planning admin page
// BRIO Migration: Using code "1b" (table compact)
import React from 'react';
import {
  BrioAnalyticsSurface,
  BrioFrame,
  BrioByCode,
  BrioTable,
  BrioColorIndicator,
  getTablePropsFromCode,
  useFullScreenPage,
  BrioFullScreenButton
} from '../../components/BrioComponents';
// Presentation code for this page
const PRESENTATION_CODE = '1b';

export default function ProjectPlanningPage({
  // Data
  getSortedProjects,
  activeClients,
  activeTimeEntries,
  projects,
  setProjects,
  // Form state
  form,
  setForm,
  // Supabase
  useSupabaseDB,
  supabaseProjects,
  // Formatters
  fmtCurr,
  currSymbol = '₹',
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);
  const { isFullScreen, toggle: toggleFullScreen, rootStyle, rootClassName } = useFullScreenPage(true);

  return (
    <div className={`brio-flex brio-flex-col brio-analytics-font ${rootClassName}`} style={{ background: '#fff', ...rootStyle }}>
      {/* PAGE HEADER BAND: Full-width white background, centered content */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">

              <p className="brio-analytics-muted brio-mt-xs">Set planned hours and view total design fees for cost variance tracking</p>
            </div>
            <BrioFullScreenButton isFullScreen={isFullScreen} onToggle={toggleFullScreen} />
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA */}
      <BrioFrame>
        <div className="brio-flex brio-flex-col brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          <BrioAnalyticsSurface padding="m">
            <BrioByCode code={PRESENTATION_CODE}>
              <BrioTable {...tableProps}>
                <thead>
                  <tr>
                    <th>Project</th>
                    <th className="text-right">Planned Hours</th>
                    <th className="text-right">Total Design Fee ({currSymbol})</th>
                    <th className="text-right">Actual Hours</th>
                    <th className="text-right">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedProjects().map(project => {
                    const client = activeClients.find(c => c.id === project.clientId);
                    const actualHours = activeTimeEntries
                      .filter(e => e.projectId === project.id)
                      .reduce((sum, e) => sum + (e.duration / 3600), 0);
                    const variance = actualHours - (project.plannedHours || 0);
                    
                    // Calculate total fee
                    const baseFee = project.budget || 0;
                    const retainerConfig = project.retainerConfig || {
                      monthlyAmount: 0,
                      totalMonths: 12,
                      status: 'inactive'
                    };
                    const ccRetainer = retainerConfig.status === 'active' 
                      ? (retainerConfig.monthlyAmount || 0) * (retainerConfig.totalMonths || 12)
                      : 0;
                    const totalFee = baseFee + ccRetainer;
                    
                    return (
                      <tr key={project.id} className="brio-row--hoverable">
                        <td>
                          <div className="brio-flex brio-items-center brio-gap-s">
                            <BrioColorIndicator color={project.color} size="compact" />
                            <div>
                              <div className="brio-font-medium">{project.projectCode || project.name}</div>
                              <div className="brio-text-xs brio-text-grey brio-italic">{client?.organisation || client?.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="text-right">
                          <input
                            type="number"
                            value={form.plannedHoursDraft?.[project.id] ?? project.plannedHours ?? ''}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              if (useSupabaseDB) {
                                setForm(prev => ({
                                  ...prev,
                                  plannedHoursDraft: {
                                    ...(prev.plannedHoursDraft || {}),
                                    [project.id]: newValue
                                  }
                                }));
                              } else {
                                setProjects(projects.map(p =>
                                  p.id === project.id
                                    ? { ...p, plannedHours: parseFloat(newValue) || 0 }
                                    : p
                                ));
                              }
                            }}
                            onBlur={async (e) => {
                              if (useSupabaseDB) {
                                const result = await supabaseProjects.updateProject(project.id, {
                                  plannedHours: parseFloat(e.target.value) || 0
                                });
                                if (result.success) {
                                  // Clear draft on success (hook auto-refetches)
                                  setForm(prev => {
                                    const newDraft = { ...(prev.plannedHoursDraft || {}) };
                                    delete newDraft[project.id];
                                    return { ...prev, plannedHoursDraft: newDraft };
                                  });
                                } else {
                                  console.error('[ProjectPlanning] Update failed:', result.error);
                                }
                              }
                            }}
                            placeholder="0"
                            className="brio-btn brio-btn-secondary"
                            style={{ width: '80px', textAlign: 'right', padding: '4px 8px' }}
                          />
                        </td>
                        <td className="text-right">
                          <div className="brio-font-medium" title="Base Design Fee + CC Retainer (if active)">
                            {fmtCurr(totalFee)}
                          </div>
                          <div className="brio-text-xs brio-text-grey brio-mt-xs">
                            {retainerConfig.status === 'active' 
                              ? `Base: ${fmtCurr(project.budget || 0)} + CC`
                              : 'Base fee only'
                            }
                          </div>
                        </td>
                        <td className="text-right">
                          <div className="brio-font-medium">{actualHours.toFixed(1)}h</div>
                        </td>
                        <td className="text-right">
                          {project.plannedHours > 0 ? (
                            <div 
                              className="brio-font-medium"
                              style={{ color: variance > 0 ? 'var(--brio-gold-hover)' : 'var(--brio-gold)' }}
                            >
                              {variance > 0 ? '+' : ''}{variance.toFixed(1)}h
                            </div>
                          ) : (
                            <span className="brio-text-grey">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </BrioTable>
            </BrioByCode>
          </BrioAnalyticsSurface>
        </div>
      </BrioFrame>
    </div>
  );
}
